const GA_ID = 'G-WH0XMRW2NR'
const CONSENT_STORAGE_KEY = 'cookieConsent'

// ─── Google Ads ────────────────────────────────────────────────────────────────
// Obtener desde: Google Ads → Herramientas → Conversiones → Detalles de la etiqueta
// Formato: AW-XXXXXXXXXX
const GOOGLE_ADS_ID = process.env.VUE_APP_GOOGLE_ADS_ID || ''

// IDs de conversión específicas (se crean en Google Ads → Conversiones)
// Ejemplo: { solicitud: 'ABC123def456', registro: 'XYZ789ghi000' }
const GOOGLE_ADS_CONVERSIONS = {
  solicitud: process.env.VUE_APP_GOOGLE_ADS_CONV_SOLICITUD || '',
  registro:  process.env.VUE_APP_GOOGLE_ADS_CONV_REGISTRO  || '',
  checkout:  process.env.VUE_APP_GOOGLE_ADS_CONV_CHECKOUT  || '',
}

function readConsentObject() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    /* ignore */
  }

  // Migración desde banner anterior (solo analíticas, sin publicidad)
  try {
    const legacy = localStorage.getItem('cookiesAccepted')
    if (legacy === 'true') return { analytics: true, advertising: false }
    if (legacy === 'false') return { analytics: false, advertising: false }
  } catch {
    /* ignore */
  }

  return null
}

function writeConsentObject(consent) {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
      ...consent,
      updatedAt: new Date().toISOString(),
    }))
    localStorage.setItem('cookiesAccepted', consent.analytics ? 'true' : 'false')
  } catch {
    /* ignore */
  }
}

export function hasAnalyticsConsent() {
  const consent = readConsentObject()
  if (consent) return !!consent.analytics
  try {
    return localStorage.getItem('cookiesAccepted') === 'true'
  } catch {
    return false
  }
}

export function hasAdvertisingConsent() {
  const consent = readConsentObject()
  if (consent) return !!consent.advertising
  return false
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }
  }
}

function updateGoogleConsentMode({ analytics, advertising }) {
  ensureGtagStub()
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: advertising ? 'granted' : 'denied',
    ad_user_data: advertising ? 'granted' : 'denied',
    ad_personalization: advertising ? 'granted' : 'denied',
  })
}

export function setDefaultGoogleConsentMode() {
  ensureGtagStub()
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })
}

export function loadGoogleAnalytics(router) {
  if (!hasAnalyticsConsent() || window.__gaLoaded) return

  window.__gaLoaded = true
  ensureGtagStub()
  updateGoogleConsentMode({
    analytics: true,
    advertising: hasAdvertisingConsent(),
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  window.gtag('js', new Date())

  const pagePath = router?.currentRoute?.value?.fullPath || window.location.pathname
  window.gtag('config', GA_ID, { anonymize_ip: true, page_path: pagePath })

  if (router && !window.__gaRouterHookInstalled) {
    window.__gaRouterHookInstalled = true
    router.afterEach((to) => {
      if (hasAnalyticsConsent() && window.gtag) {
        window.gtag('config', GA_ID, { page_path: to.fullPath })
      }
    })
  }
}

/** Reservado para cuando se active AdSense en producción */
export function loadGoogleAdSense() {
  if (!hasAdvertisingConsent() || window.__adsenseLoaded) return
  // La carga del script se implementará con el componente GoogleAdSlot real
}

// ─── Google Ads Conversion Tracking ───────────────────────────────────────────
/**
 * Carga el tag de Google Ads y lo vincula con GA4.
 * Se llama cuando el usuario da consentimiento de publicidad.
 * Requiere VUE_APP_GOOGLE_ADS_ID en .env.
 */
export function loadGoogleAds() {
  if (!GOOGLE_ADS_ID) return                     // sin ID configurado, no hacer nada
  if (!hasAdvertisingConsent()) return           // sin consentimiento, no cargar
  if (window.__gadsLoaded) return               // evitar cargas dobles
  window.__gadsLoaded = true

  ensureGtagStub()

  // Configurar la propiedad de Google Ads (vinculada con GA4 si ya está cargado)
  window.gtag('config', GOOGLE_ADS_ID, {
    allow_enhanced_conversions: true,
  })
}

/**
 * Dispara una conversión de Google Ads.
 * Solo funciona si el usuario aceptó cookies de publicidad.
 * @param {string} conversionLabel - Label de la conversión (de GOOGLE_ADS_CONVERSIONS)
 * @param {object} params - { value, currency, transaction_id }
 */
export function trackGoogleAdsConversion(conversionLabel, params = {}) {
  if (!GOOGLE_ADS_ID || !conversionLabel) return
  if (!hasAdvertisingConsent()) return
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
    value: params.value || 0,
    currency: params.currency || 'EUR',
    ...(params.transaction_id ? { transaction_id: params.transaction_id } : {}),
  })
}

// Exportar los IDs para que useAnalytics.js pueda usarlos
export { GOOGLE_ADS_CONVERSIONS }

export function loadMetaPixel() {
  if (typeof window !== 'undefined' && window.__loadMetaPixel) {
    window.__loadMetaPixel()
  }
}

export function grantAllCookieConsent(router) {
  writeConsentObject({ analytics: true, advertising: true })
  updateGoogleConsentMode({ analytics: true, advertising: true })
  loadGoogleAnalytics(router)
  loadGoogleAdSense()
  loadGoogleAds()
  loadMetaPixel()
  if (router?.currentRoute?.value && window.gtag) {
    window.gtag('config', GA_ID, { page_path: router.currentRoute.value.fullPath })
  }
}

export function grantAnalyticsConsent(router) {
  grantAllCookieConsent(router)
}

export function revokeOptionalCookieConsent() {
  writeConsentObject({ analytics: false, advertising: false })
  updateGoogleConsentMode({ analytics: false, advertising: false })
}

export function revokeAnalyticsConsent() {
  revokeOptionalCookieConsent()
}

export function hasCookieConsentChoice() {
  try {
    if (localStorage.getItem(CONSENT_STORAGE_KEY)) return true
    if (localStorage.getItem('cookiesAccepted') !== null) return true
  } catch {
    /* ignore */
  }
  return false
}
